import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, FileText, Hash, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { OnboardingShell } from '../../components/venue/onboarding/OnboardingShell';
import { PremiumField } from '../../components/venue/onboarding/PremiumField';
import { resolveStepCounter } from '../../utils/venueOnboardingProgress';
import { useOnboardingSaveActions } from '../../utils/useOnboardingSaveActions';
import { titleCaseName } from '../../utils/nameFormatters';
import { lookupVat, normalizeAfm } from '../../utils/aadeVatLookup';

/**
 * Merged Owner + Business page. Previously Step 1 (names) and Step 2
 * (restaurant name + legal name + VAT) were split across two screens —
 * that felt like paperwork before the venue owner saw any value. Combining
 * them into a single quick-hit form is part of the "fewer forms up front,
 * see the app sooner" restructure.
 */
export default function VenueOnboardingStep1() {
  const navigate = useNavigate();
  const { step, total } = resolveStepCounter(1);

  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('venueOnboarding');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        firstName: parsed.firstName || '',
        lastName: parsed.lastName || '',
        restaurantName: parsed.restaurantName || '',
        legalBusinessName: parsed.legalBusinessName || '',
        businessVat: parsed.businessVat || '',
      };
    }
    return {
      firstName: '',
      lastName: '',
      restaurantName: '',
      legalBusinessName: '',
      businessVat: '',
    };
  });



  useEffect(() => {
    const existing = sessionStorage.getItem('venueOnboarding');
    const parsed = existing ? JSON.parse(existing) : {};
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...parsed,
      firstName: formData.firstName,
      lastName: formData.lastName,
      restaurantName: formData.restaurantName,
      legalBusinessName: formData.legalBusinessName,
      businessVat: formData.businessVat,
    }));
  }, [formData]);

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  // ---- AADE VAT auto-fill ----------------------------------------
  // status: idle | loading | success | error
  const [vatStatus, setVatStatus] = useState('idle');
  const [vatError, setVatError] = useState('');
  const [vatSuccessMeta, setVatSuccessMeta] = useState(null); // { legalName, taxOffice }
  // Remember the last ΑΦΜ we successfully resolved so we don't spam AADE
  // when the user tabs in/out of the field without changing anything.
  const lastLookedUpAfm = useRef('');

  const runVatLookup = async (rawVat) => {
    const afm = normalizeAfm(rawVat);
    if (!afm) {
      // Not enough digits yet — clear any stale success/error state so
      // the field looks neutral again.
      setVatStatus('idle');
      setVatError('');
      setVatSuccessMeta(null);
      return;
    }
    if (afm === lastLookedUpAfm.current && vatStatus === 'success') {
      return; // no-op if user tabbed away and back without changing it.
    }

    setVatStatus('loading');
    setVatError('');
    const result = await lookupVat(afm);
    if (!result.ok) {
      setVatStatus('error');
      setVatSuccessMeta(null);
      if (result.code === 'TAXPAYER_NOT_FOUND' || result.code === 'INVALID_AFM') {
        // Both codes mean the same thing from a venue-owner's perspective:
        // AADE didn't accept the 9-digit number they typed. INVALID_AFM
        // from AADE is different from our own client-side digit-count
        // check (that path never sends a request), so the copy should
        // focus on the ΑΦΜ being unknown rather than badly formatted.
        setVatError("AADE doesn't recognise this ΑΦΜ — double-check the digits.");
      } else if (result.code === 'AADE_RATE_LIMIT') {
        setVatError('AADE monthly lookup limit reached. You can still continue and fill things in manually.');
      } else {
        setVatError("Couldn't reach AADE right now. You can still continue manually.");
      }
      return;
    }

    lastLookedUpAfm.current = afm;
    setVatStatus('success');
    setVatSuccessMeta({
      legalName: result.data.legal_name || '',
      taxOffice: result.data.tax_office || '',
      active: result.data.vat_status?.active,
    });

    // Auto-fill empty fields only. Never clobber what the user has typed.
    const legalName = (result.data.legal_name || '').trim();
    const tradeName = (result.data.trade_name || result.data.name || '').trim();
    setFormData((prev) => ({
      ...prev,
      legalBusinessName: prev.legalBusinessName.trim() ? prev.legalBusinessName : legalName,
      restaurantName: prev.restaurantName.trim() ? prev.restaurantName : titleCaseName(tradeName || legalName),
      businessVat: afm, // canonicalise the field to the 9-digit form.
    }));

    // Stash AADE-supplied address + category into sessionStorage so
    // Step 2 (Location) and later Setup Mode (Vibe) can pre-fill.
    const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    const addr = result.data.address || {};
    const streetLine = [addr.street, addr.number].filter(Boolean).join(' ').trim();
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...existing,
      // Only fill empty address fields — don't overwrite if the user
      // has already selected a location on Step 2.
      country: existing.country || 'Greece',
      city: existing.city || addr.area || '',
      zipCode: existing.zipCode || addr.postal_code || '',
      street: existing.street || streetLine,
      addressSearchQuery: existing.addressSearchQuery
        || [streetLine, addr.postal_code, addr.area, 'Greece'].filter(Boolean).join(', '),
      aade: {
        afm,
        kad: result.data.category?.kad || '',
        kadDescription: result.data.category?.description || '',
        taxOffice: result.data.tax_office || '',
        legalName: result.data.legal_name || '',
        vatActive: !!result.data.vat_status?.active,
        lookedUpAt: result.data.looked_up_at,
      },
    }));
  };

  const isValid =
    formData.firstName.trim()
    && formData.lastName.trim()
    && formData.restaurantName.trim()
    && formData.legalBusinessName.trim()
    && formData.businessVat.trim();

  const { autoSave } = useOnboardingSaveActions();

  const handleNext = async () => {
    if (!isValid) return;
    // Title-case the human names + public venue name so downstream surfaces
    // (chats, member lists, guest map) look polished regardless of how the
    // owner typed them. Legal name + VAT are kept verbatim — those are
    // registered exactly as issued by the tax authority.
    const cleanedFirst = titleCaseName(formData.firstName);
    const cleanedLast = titleCaseName(formData.lastName);
    const cleanedRestaurant = titleCaseName(formData.restaurantName);
    if (
      cleanedFirst !== formData.firstName
      || cleanedLast !== formData.lastName
      || cleanedRestaurant !== formData.restaurantName
    ) {
      setFormData((prev) => ({
        ...prev,
        firstName: cleanedFirst,
        lastName: cleanedLast,
        restaurantName: cleanedRestaurant,
      }));
    }
    const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...existing,
      firstName: cleanedFirst,
      lastName: cleanedLast,
      restaurantName: cleanedRestaurant,
      legalBusinessName: formData.legalBusinessName,
      businessVat: formData.businessVat,
    }));
    await autoSave(1);
    navigate('/venue/onboarding/2');
  };

  const handleBack = () => navigate('/venue/intro');

  return (
    <OnboardingShell
      testId="venue-step1"
      step={step}
      totalSteps={total}
      title="Let's get you set up"
      subtitle="A few quick details about you and your venue. We use these for direct communication, invoices and tax documents."
      primaryLabel="Continue"
      primaryTestId="venue-step1-next"
      primaryDisabled={!isValid}
      onPrimary={handleNext}
      onBack={handleBack}
    >
      <PremiumField
        label="Business VAT / ΑΦΜ"
        value={formData.businessVat}
        onChange={(v) => {
          updateField('businessVat', v);
          if (vatStatus !== 'idle') {
            setVatStatus('idle');
            setVatError('');
            setVatSuccessMeta(null);
          }
        }}
        onBlur={(e) => runVatLookup(e.target.value)}
        placeholder="e.g. EL803335274"
        testId="venue-step1-vat"
        icon={Hash}
        required
        autoComplete="off"
        inputMode="numeric"
        hint="Your venue's business VAT — not your personal one. We'll fetch its legal name and address from AADE (Greek Tax Authority) so you don't have to type them."
      />

      {vatStatus === 'loading' ? (
        <div
          data-testid="venue-step1-vat-loading"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: '-8px 4px 12px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 12.5,
            color: 'var(--text-3)',
          }}
        >
          <Loader2 size={14} className="animate-spin" strokeWidth={2.2} />
          Looking up ΑΦΜ with AADE…
        </div>
      ) : null}

      {vatStatus === 'success' && vatSuccessMeta ? (
        <div
          data-testid="venue-step1-vat-success"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            margin: '-8px 4px 12px',
            padding: '10px 12px',
            background: 'var(--status-success-surface)',
            border: '1px solid rgba(31, 142, 42, 0.18)',
            borderRadius: 12,
          }}
        >
          <CheckCircle2 size={16} color="var(--status-success-text)" strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 700, color: 'var(--status-success-text)' }}>
              Verified with AADE
              {vatSuccessMeta.active === false ? ' (deactivated)' : ''}
            </p>
            <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12.5, color: 'var(--text-2)' }}>
              {vatSuccessMeta.legalName}
              {vatSuccessMeta.taxOffice ? ` · ${vatSuccessMeta.taxOffice}` : ''}
            </p>
          </div>
        </div>
      ) : null}

      {vatStatus === 'error' && vatError ? (
        <div
          data-testid="venue-step1-vat-error"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            margin: '-8px 4px 12px',
            padding: '10px 12px',
            background: 'var(--status-warning-surface)',
            border: '1px solid rgba(231, 158, 38, 0.20)',
            borderRadius: 12,
          }}
        >
          <AlertCircle size={16} color="var(--status-warning-text)" strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 12.5, color: 'var(--status-warning-text)' }}>
            {vatError}
          </p>
        </div>
      ) : null}

      <PremiumField
        label="Legal business name"
        value={formData.legalBusinessName}
        onChange={(v) => updateField('legalBusinessName', v)}
        placeholder="e.g. Ora Hospitality LLC"
        testId="venue-step1-legal-name"
        icon={FileText}
        required
      />
      <PremiumField
        label="Public venue name"
        value={formData.restaurantName}
        onChange={(v) => updateField('restaurantName', v)}
        onBlur={(e) => updateField('restaurantName', titleCaseName(e.target.value))}
        placeholder="e.g. Taverna Ora"
        testId="venue-step1-restaurant-name"
        icon={Briefcase}
        required
        hint="This is how guests will see you in the Allure app."
      />

      {/* --- Legal signer name (for the Venue Partner Agreement) -------
          The signup nickname ("Maria K.") stays untouched — that's the
          public/chat identity. Here we capture the full legal name that
          appears on the agreement they sign at the end of onboarding.
      */}
      <div
        data-testid="venue-step1-legal-name-header"
        style={{
          marginTop: 6,
          marginBottom: 8,
          padding: '10px 12px',
          borderRadius: 12,
          background: 'var(--accent-surface-soft)',
          border: '1px solid rgba(120, 88, 214, 0.14)',
        }}
      >
        <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 12.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent-purple)' }}>
          Your legal name
        </p>
        <p style={{ margin: '4px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)' }}>
          Your legal name won&apos;t be visible publicly.
        </p>
      </div>

      <PremiumField
        label="First name"
        value={formData.firstName}
        onChange={(v) => updateField('firstName', v)}
        onBlur={(e) => updateField('firstName', titleCaseName(e.target.value))}
        placeholder="e.g. Maria"
        testId="venue-step1-first-name"
        icon={User}
        required
        autoComplete="given-name"
      />
      <PremiumField
        label="Last name"
        value={formData.lastName}
        onChange={(v) => updateField('lastName', v)}
        onBlur={(e) => updateField('lastName', titleCaseName(e.target.value))}
        placeholder="e.g. Papadopoulou"
        testId="venue-step1-last-name"
        icon={User}
        required
        autoComplete="family-name"
      />
    </OnboardingShell>
  );
}
